import { useTranslation } from 'next-i18next'
import { useCallback, useState, FC, Fragment } from 'react'

import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch'

import dynamic from 'next/dynamic'

import mathVaultAnalytics from '~/analytics/mathVault/mathVaultAnalytics'
import { MathVaultPathObject } from '~/analytics/mathVault/mathVaultEvents.types'

import ResponsiveImage from '~/components/Image/ResponsiveImage'
import useActivityPath from '~/math-vault/hooks/useActivityPath'

import SVGImage from './Image/SVGImage'

const Portal = dynamic(() => import('~/components/Portal'), { ssr: false })

const mediaQuery = [{ screen: '(min-width: 300px)', width: '36px' }]

const MathVaultAwsImageModal: FC<{ props: any; onClose?: () => void; activityPath: MathVaultPathObject }> = ({
  props,
  onClose = () => {},
}) => {
  const { t } = useTranslation('mathVault')
  return (
    <>
      <div
        className="relative bg-white h-full w-full md:h-5/6 md:w-5/6 lg:h-3/4 lg:w-3/4
        place-self-center rounded z-10 p-3 flex flex-col drop-shadow-lg"
      >
        <TransformWrapper>
          {({ zoomIn, zoomOut }) => (
            <Fragment>
              <div className="h-12 flex flex-row items-start">
                <div className="flex-1 flex flex-col">
                  <div className="flex flex-row gap-6">
                    <ResponsiveImage
                      alt={t('zoom-in-btn')}
                      className="cursor-pointer"
                      height={36}
                      mediaQuery={mediaQuery}
                      onClick={() => {
                        zoomIn()
                        // mathVaultAnalytics.clickOnZoomInBtn(activityPath)
                      }}
                      srcKey="Math Vault/icons/ZoomInIcon.svg"
                      width={36}
                    />
                    <ResponsiveImage
                      alt={t('zoom-out-btn')}
                      className="cursor-pointer"
                      height={36}
                      mediaQuery={mediaQuery}
                      onClick={() => {
                        zoomOut()
                        // mathVaultAnalytics.clickOnZoomOutBtn(activityPath)
                      }}
                      srcKey="Math Vault/icons/ZoomOutIcon.svg"
                      width={36}
                    />
                  </div>
                </div>
                <ResponsiveImage
                  alt={t('close-btn')}
                  className="cursor-pointer"
                  height={24}
                  mediaQuery={mediaQuery}
                  onClick={onClose}
                  srcKey="Math Vault/icons/close-btn.svg"
                  width={24}
                />
              </div>
              <TransformComponent
                contentClass="flex-1 !w-full cursor-pointer flex justify-center content-center"
                wrapperClass="flex-1 flex flex-col !w-full rounded"
              >
                <div className="h-full">
                  <ResponsiveImage
                    alt={props.node.alt}
                    height={props.node.height}
                    imageClassName="h-full object-contain"
                    loading="eager"
                    srcKey={props.node.srcKey}
                    width={props.node.width}
                  />
                </div>
              </TransformComponent>
            </Fragment>
          )}
        </TransformWrapper>
      </div>
      <div className="bg-white opacity-30 absolute top-0 w-full h-full" onClick={onClose} />
    </>
  )
}

const MathVaultAwsImage: FC<any> = (props) => {
  const { zoomable } = props.node
  const [modalOpened, setModalOpened] = useState(false)

  const { currentActivityPathObject } = useActivityPath()

  const onImageClick = useCallback(() => {
    if (!zoomable) {
      return
    }

    mathVaultAnalytics.clickOnQuestionImage(currentActivityPathObject)
    setModalOpened(!modalOpened)
  }, [zoomable, currentActivityPathObject, modalOpened])

  const handleOnClose = (): void => {
    setModalOpened(false)
  }

  function checkSvgUrl(url: string): boolean {
    return /\.svg$/.test(url)
  }

  return (
    <>
      <figure className="h-full w-full " onClick={onImageClick}>
        {checkSvgUrl(props.node.srcKey) ? (
          <SVGImage
            alt={props.node.alt}
            className="h-full block"
            height={props.node.height ?? 185}
            imageClassName="mx-auto max-w-full h-full object-contain"
            loading="eager"
            srcKey={props.node.srcKey}
            width={props.node.width}
          />
        ) : (
          <ResponsiveImage
            alt={props.node.alt}
            height={props.node.height ?? 370}
            imageClassName="mx-auto max-w-full max-h-full object-contain"
            loading="eager"
            srcKey={props.node.srcKey}
            width={props.node.width}
          />
        )}
      </figure>

      {modalOpened && (
        <Portal targetDomId="__next" wrapperClassNames="fixed z-modal-static inset-0 grid p-2">
          <MathVaultAwsImageModal activityPath={currentActivityPathObject} onClose={handleOnClose} props={props} />
        </Portal>
      )}
    </>
  )
}

export default MathVaultAwsImage
